'use client';

/**
 * Storage primitives behind persisted-upload-store.ts: one IndexedDB database
 * (upload metadata, and file bytes when OPFS is missing) and one OPFS
 * directory (file bytes). Every helper resolves `null`/`false` instead of
 * throwing when the backend is unavailable, so the store can degrade.
 */

const DATABASE = 'imai-background-uploads';
const DATABASE_VERSION = 1;
export const UPLOADS_STORE = 'uploads';
export const BLOBS_STORE = 'blobs';
const OPFS_DIRECTORY = 'imai-background-uploads';
/** An open still pending by then waits behind another tab's upgrade: give up. */
const OPEN_TIMEOUT_MS = 2_000;

type StoreName = typeof UPLOADS_STORE | typeof BLOBS_STORE;

/** The parts of `navigator.storage` the store uses (tests pass their own). */
export type UploadBlobStorage = {
  getDirectory?: () => Promise<FileSystemDirectoryHandle>;
  persist?: () => Promise<boolean>;
};

export function openUploadDatabase(
  factory: IDBFactory | undefined
): Promise<IDBDatabase | null> {
  if (!factory) return Promise.resolve(null);
  return new Promise<IDBDatabase | null>((resolve) => {
    let settled = false;
    const finish = (db: IDBDatabase | null) => {
      if (settled) {
        db?.close();
        return;
      }
      settled = true;
      resolve(db);
    };
    setTimeout(() => finish(null), OPEN_TIMEOUT_MS);
    try {
      const request = factory.open(DATABASE, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(UPLOADS_STORE))
          db.createObjectStore(UPLOADS_STORE, { keyPath: 'uploadId' });
        if (!db.objectStoreNames.contains(BLOBS_STORE))
          db.createObjectStore(BLOBS_STORE);
      };
      request.onsuccess = () => finish(request.result);
      request.onerror = () => finish(null);
      request.onblocked = () => finish(null);
    } catch {
      finish(null);
    }
  });
}

/**
 * Run one request in its own transaction and resolve its result once the
 * transaction commits (a write is only durable then); reject on failure.
 */
export function runUploadRequest<T>(
  db: IDBDatabase,
  storeName: StoreName,
  mode: IDBTransactionMode,
  makeRequest: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    try {
      const transaction = db.transaction(storeName, mode);
      const request = makeRequest(transaction.objectStore(storeName));
      const fail = () =>
        reject(transaction.error ?? new Error('IndexedDB transaction failed'));
      transaction.oncomplete = () => resolve(request.result);
      transaction.onerror = fail;
      transaction.onabort = fail;
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Read one row and write back what `mutate` returns, in one transaction, so
 * two patches to the same upload cannot overwrite each other. Resolves the
 * written row, or null when there was no row.
 */
export function mutateUploadRow<T>(
  db: IDBDatabase,
  key: string,
  mutate: (current: unknown) => T | null
): Promise<T | null> {
  return new Promise<T | null>((resolve, reject) => {
    try {
      const transaction = db.transaction(UPLOADS_STORE, 'readwrite');
      const store = transaction.objectStore(UPLOADS_STORE);
      let written: T | null = null;
      const read = store.get(key);
      read.onsuccess = () => {
        if (read.result === undefined) return;
        written = mutate(read.result);
        if (written !== null) store.put(written);
      };
      const fail = () =>
        reject(transaction.error ?? new Error('IndexedDB transaction failed'));
      transaction.oncomplete = () => resolve(written);
      transaction.onerror = fail;
      transaction.onabort = fail;
    } catch (error) {
      reject(error);
    }
  });
}

async function opfsDirectory(
  storage: UploadBlobStorage | undefined,
  create: boolean
): Promise<FileSystemDirectoryHandle | null> {
  if (typeof storage?.getDirectory !== 'function') return null;
  try {
    const root = await storage.getDirectory();
    return await root.getDirectoryHandle(OPFS_DIRECTORY, { create });
  } catch {
    return null;
  }
}

/** Stream the bytes into one OPFS file; false when OPFS cannot take them. */
export async function writeOpfsBlob(
  storage: UploadBlobStorage | undefined,
  name: string,
  blob: Blob
): Promise<boolean> {
  const directory = await opfsDirectory(storage, true);
  if (!directory) return false;
  try {
    const handle = await directory.getFileHandle(name, { create: true });
    if (typeof handle.createWritable !== 'function') {
      await directory.removeEntry(name).catch(() => undefined);
      return false;
    }
    const writable = await handle.createWritable();
    // pipeTo closes the writable on success and aborts it on failure.
    await blob.stream().pipeTo(writable);
    return true;
  } catch {
    // A half-written file (quota) must not pass for the upload's bytes.
    await directory.removeEntry(name).catch(() => undefined);
    return false;
  }
}

export async function readOpfsBlob(
  storage: UploadBlobStorage | undefined,
  name: string
): Promise<Blob | null> {
  const directory = await opfsDirectory(storage, false);
  if (!directory) return null;
  try {
    const handle = await directory.getFileHandle(name);
    return await handle.getFile();
  } catch {
    return null;
  }
}

export async function removeOpfsBlob(
  storage: UploadBlobStorage | undefined,
  name: string
): Promise<void> {
  const directory = await opfsDirectory(storage, false);
  if (!directory) return;
  await directory.removeEntry(name).catch(() => undefined);
}
